import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Supabase 인증 서버는 같은 주소로 60초 안에 두 번 요청하면 429로 거부한다.
// 발송이 성공하면 그 시간만큼 버튼을 잠가, 재클릭이 에러로 보이는 일을 막는다.
const RESEND_COOLDOWN_SECONDS = 60;

// 발송 실패는 원인이 서로 다르고, 사용자가 취할 행동도 다르다.
// "잠시 후 다시 시도"로 뭉개면 메일을 못 받는 사용자가 무한히 버튼만 누른다.
function describeResetError(err: any): string {
  const code = err?.code || err?.error_code || '';
  const message = String(err?.message || '');

  if (code === 'over_email_send_rate_limit' || err?.status === 429) {
    const wait = message.match(/after (\d+) seconds?/);
    const seconds = wait ? wait[1] : `${RESEND_COOLDOWN_SECONDS}`;
    return `조금 전에 이미 발송을 요청했습니다. ${seconds}초 후에 다시 시도해주세요. 먼저 메일함과 스팸함을 확인해보세요.`;
  }

  if (code === 'email_address_invalid' || /address .* is invalid/i.test(message)) {
    return '이 이메일 주소로는 메일을 보낼 수 없습니다. 메일 서버가 수신을 거부했습니다.\n센터(031-866-5670)로 연락하시면 비밀번호를 재설정해 드립니다.';
  }

  return '비밀번호 재설정 이메일 발송에 실패했습니다. 잠시 후 다시 시도해주세요.\n계속 실패하면 센터(031-866-5670)로 연락해주세요.';
}

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [maskedUsername, setMaskedUsername] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setMaskedUsername('');

    if (cooldown > 0) {
      setError(`조금 전에 발송을 요청했습니다. ${cooldown}초 후에 다시 시도해주세요.`);
      return;
    }

    // 가입 이메일은 소문자로 저장된다. 'Gmail.com'처럼 입력해도 같은 계정을 찾도록 맞춘다.
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError('이메일을 입력해주세요.');
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      setError('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      // RPC 함수로 마스킹된 아이디 조회
      const { data: maskedName, error: rpcError } = await supabase
        .rpc('get_masked_username_by_email', { user_email: normalizedEmail });

      if (rpcError || !maskedName) {
        setError('해당 이메일로 가입된 계정이 없습니다.');
        setLoading(false);
        return;
      }

      // 비밀번호 재설정 이메일 발송
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        console.error('Password reset error:', resetError);
        setError(describeResetError(resetError));
      } else {
        setMaskedUsername(maskedName);
        setSuccess(true);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex-grow bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32 sm:pt-28">
        <div className="max-w-md w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              비밀번호 찾기
            </h1>
            <p className="text-sm text-gray-600">
              가입 시 등록한 이메일을 입력하세요
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded p-8">
            {success ? (
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  이메일을 발송했습니다
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {email}로 비밀번호 재설정 링크를 발송했습니다.<br />
                  이메일을 확인해주세요.
                </p>
                {maskedUsername && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
                    <p className="text-sm text-gray-700">
                      회원님의 아이디: <span className="font-bold text-green-600">{maskedUsername}</span>
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-500 mb-6">
                  이메일이 오지 않으면 스팸함을 확인해주세요.
                </p>
                <Link
                  to="/login"
                  className="inline-block w-full py-3 px-4 border border-transparent text-base font-medium rounded text-white bg-green-600 hover:bg-green-700 text-center"
                >
                  로그인 페이지로 돌아가기
                </Link>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    이메일
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="가입 시 등록한 이메일을 입력하세요"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                {error && (
                  <div className="rounded bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading || cooldown > 0}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading
                      ? '발송 중...'
                      : cooldown > 0
                        ? `다시 시도 (${cooldown}초)`
                        : '비밀번호 재설정 이메일 발송'}
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-gray-200">
                  <Link
                    to="/login"
                    className="text-sm text-green-600 hover:text-green-700 font-medium hover:underline"
                  >
                    로그인 페이지로 돌아가기
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
